/**
 * Today's summary, inside the Overview's Rentals Summary widget. GET
 * /surface-rentals/ai/daily-summary (api#859; version 2 since api#1094).
 *
 * Back on the Overview once its numbers were the server's (operator,
 * 2026-10-15: "no wrong numbers on Overview"). Before api#1094 the model wrote
 * "7 active rental orders … 18 assets", counting a closed order and an
 * upcoming one, beside this widget's 6 and 15. Version 2 sends
 * `active_jobs_summary` written by the server from the same counts the
 * dashboard reads, and each highlight's `day_count` from the server; the model
 * writes only the notes and the action items. Nothing here counts anything.
 *
 * Moved from the retired Rentals dashboard (SurfaceRentalsDashboard.tsx, in git
 * history), with the same defensive reads: the model's parts are a request, not
 * a contract, so every item is read as it comes and anything unreadable is
 * dropped rather than shown raw. No Regenerate here: the Overview reads the
 * cached summary only.
 *
 * Cached per tenant day. `generated_at` sits BESIDE `data` on the envelope. The
 * route serves admin, office staff, supervisors and dispatchers; for anyone else
 * the section is not drawn (no request, no refusal). A 502 is an unreadable
 * model reply: one line, and the figures above do not depend on it.
 */
import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { api } from '../../../api/client';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { formatInstant } from '../../../lib/calendar-date';
import { classifyLoadFailure } from '../../../lib/load-failure';
import { LoadFailureNotice } from '../../ui/LoadFailureNotice';
import { WIDGET_EYEBROW } from '../WidgetPanel';

export const SUMMARY_ROLES = new Set(['admin', 'office_staff', 'supervisor', 'dispatcher']);

interface DailySummary {
  data: Record<string, unknown>;
  generated_at: string | null;
}
const toDailySummary = (body: unknown): DailySummary => {
  const b = (body ?? {}) as Record<string, unknown>;
  const data = b.data && typeof b.data === 'object' ? (b.data as Record<string, unknown>) : {};
  return { data, generated_at: typeof b.generated_at === 'string' ? b.generated_at : null };
};

type SummaryLine = { key: string; text: string };
function summaryLines(raw: unknown, describe: (o: Record<string, unknown>) => string | null): SummaryLine[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, i) => {
      if (typeof item === 'string') return item.trim() ? { key: String(i), text: item } : null;
      if (item && typeof item === 'object') {
        const text = describe(item as Record<string, unknown>);
        return text ? { key: String(i), text } : null;
      }
      return null;
    })
    .filter((x): x is SummaryLine => x != null);
}
const s = (v: unknown) => (typeof v === 'string' && v.trim() ? v : typeof v === 'number' ? String(v) : null);

const OVERALL: Record<string, { label: string; fill: string }> = {
  green: { label: 'On track', fill: 'bg-fi-signal-green' },
  amber: { label: 'Watch', fill: 'bg-fi-signal-amber' },
  red: { label: 'Act now', fill: 'bg-fi-signal-red' },
};
const badge = 'inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.65rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink';

export default function RentalsAiSummary() {
  const { user } = useAuth();
  const timeZone = useTenantTimeZone();
  const role = typeof (user as any)?.role === 'string' ? (user as any).role : null;
  const canRead = role != null && SUMMARY_ROLES.has(role);

  // No retry: a 502 is an unreadable model reply, and the next load is the retry.
  const q = useQuery({
    queryKey: ['rentals-daily-summary'],
    queryFn: () => api.rentals.ai.dailySummary().then((res) => toDailySummary(res.data)),
    enabled: canRead,
    staleTime: 60_000,
    retry: false,
  });
  if (!canRead) return null;

  const summary = q.data?.data;
  const asOf = formatInstant(q.data?.generated_at ?? null, timeZone);
  const refused = q.isError && classifyLoadFailure(q.error) === 'refused';
  const overall = typeof summary?.overall_status === 'string' ? OVERALL[summary.overall_status] : undefined;
  const groups = [
    { title: 'PM coming due', lines: summaryLines(summary?.pm_alerts, (o) => [s(o.asset_name), s(o.recommendation)].filter(Boolean).join(': ') || null) },
    {
      title: 'Overdue returns',
      lines: summaryLines(summary?.overdue_alerts, (o) => {
        const who = [s(o.order_number), s(o.customer)].filter(Boolean).join(' · ');
        const days = s(o.days_overdue);
        return who ? `${who}${days != null ? ` · ${days} days overdue` : ''}` : null;
      }),
    },
    {
      title: 'Moving today',
      lines: summaryLines(summary?.dispatch_today, (o) =>
        [s(o.type), s(o.order_number), s(o.destination) ? `to ${s(o.destination)}` : null, s(o.driver)].filter(Boolean).join(' · ') || null,
      ),
    },
    { title: 'Action items', lines: summaryLines(summary?.action_items, (o) => s(o.text)) },
  ].filter((g) => g.lines.length > 0);

  return (
    <section aria-labelledby="rentals-ai-summary-title" data-testid="rentals-ai-summary" className="rounded-fi-control border border-fieldiq-border px-3 py-3">
      <p className={WIDGET_EYEBROW}>Written by AI from today's orders, PM dates and dispatches</p>
      <h3 id="rentals-ai-summary-title" className="mt-0.5 text-sm font-extrabold text-fieldiq-text">Today's summary</h3>
      <div className="mt-2 space-y-3">
        {q.isLoading ? (
          <div className="space-y-2" aria-hidden="true">
            <div className="h-4 w-3/4 animate-pulse rounded bg-fieldiq-dark/60" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-fieldiq-dark/60" />
          </div>
        ) : refused ? (
          <LoadFailureNotice error={q.error} subject="today's summary" allowed="admins, office staff, supervisors and dispatchers" />
        ) : q.isError ? (
          <p data-testid="rentals-ai-summary-failed" className="text-sm text-fieldiq-muted">
            The summary could not be generated just now. Try again in a moment. The figures above do not depend on it.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {overall && <span className={`${badge} ${overall.fill}`}>{overall.label}</span>}
              <p data-testid="rentals-ai-summary-headline" className="text-sm text-fieldiq-text">{s(summary?.active_jobs_summary) ?? '—'}</p>
            </div>
            {groups.map((g) => (
              <div key={g.title}>
                <p className={`${WIDGET_EYEBROW} mb-1`}>{g.title}</p>
                <ul className="space-y-1">
                  {g.lines.map((l) => (
                    <li key={l.key} className="flex items-start gap-2 text-sm text-fieldiq-text">
                      <Sparkles className="mt-0.5 size-3.5 shrink-0 text-fieldiq-muted" aria-hidden="true" />
                      {l.text}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {/* When the cached summary was written, on the tenant's clock. */}
            <p data-testid="rentals-ai-summary-as-of" className="text-[11px] text-fieldiq-muted">As of {asOf ?? '—'}</p>
          </>
        )}
      </div>
    </section>
  );
}
